import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  ComposedChart,
  Bar,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  usePlotArea,
} from 'recharts';
import {
  useWeatherInPortApi,
  WeatherInPortResponse,
  MonthlyWeatherData,
  MONTH_KEYS,
  MONTH_NAMES,
  INTENSITY_LABELS,
  INTENSITY_THRESHOLDS,
} from '../../api/ApiWeatherInPort/ApiWeatherInPort';
import './WeatherInPort.scss';

const INTENSITY_KEYS = ['pct_dry', 'pct_light_drizzle', 'pct_light', 'pct_moderate_low', 'pct_moderate', 'pct_moderate_high', 'pct_heavy'] as const;

// Drizzle (0.1-0.2 mm/h) is a sub-bin of pct_light (0.1-1.0); the displayed
// "Light" value is always the disjoint 0.2-1.0 share.
const getDrizzle = (m: MonthlyWeatherData): number => m.pct_light_drizzle ?? 0;
const getLightAboveDrizzle = (m: MonthlyWeatherData): number => m.pct_light - getDrizzle(m);

// Fixed YAxis domain of the monthly chart — the overlay maps values with it.
const Y_DOMAIN_MAX = 100;

interface SpreadRow {
  spanMin: number | null;
  spanMax: number | null;
  stdBand: number[] | null;
}

/** Year-to-year spread drawn manually inside the chart: the built-in
 * Line/Area plot at category CENTERS, so every run started mid-bar. This
 * extends each run horizontally to the BAR edges (bars fill 80% of the band
 * with recharts' default 10% category gap). The invisible Line/Area stay
 * mounted purely to feed the tooltip. */
const SpreadOverlay: React.FC<{ rows: SpreadRow[] }> = ({ rows }) => {
  const plot = usePlotArea();
  if (!plot || rows.length === 0) return null;
  const band = plot.width / rows.length;
  const halfBar = band * 0.4;
  const cx = (i: number) => plot.x + band * (i + 0.5);
  const cy = (v: number) =>
    plot.y + plot.height * (1 - Math.min(v, Y_DOMAIN_MAX) / Y_DOMAIN_MAX);

  // Consecutive months with data, split on gaps (mirrors the app's PrecipChart).
  const runs = <T,>(pick: (r: SpreadRow) => T | null | undefined): { i: number; v: T }[][] => {
    const out: { i: number; v: T }[][] = [];
    let cur: { i: number; v: T }[] = [];
    rows.forEach((r, i) => {
      const v = pick(r);
      if (v == null) {
        if (cur.length) out.push(cur);
        cur = [];
      } else {
        cur.push({ i, v });
      }
    });
    if (cur.length) out.push(cur);
    return out;
  };

  const linePoints = (run: { i: number; v: number }[]): string => {
    const first = run[0];
    const last = run[run.length - 1];
    return [
      `${cx(first.i) - halfBar},${cy(first.v)}`,
      ...run.map((p) => `${cx(p.i)},${cy(p.v)}`),
      `${cx(last.i) + halfBar},${cy(last.v)}`,
    ].join(' ');
  };

  const bandPoints = (run: { i: number; v: number[] }[]): string => {
    const first = run[0];
    const last = run[run.length - 1];
    const ext = [
      { x: cx(first.i) - halfBar, lo: first.v[0], hi: first.v[1] },
      ...run.map((p) => ({ x: cx(p.i), lo: p.v[0], hi: p.v[1] })),
      { x: cx(last.i) + halfBar, lo: last.v[0], hi: last.v[1] },
    ];
    const upper = ext.map((p) => `${p.x},${cy(p.hi)}`);
    const lower = [...ext].reverse().map((p) => `${p.x},${cy(p.lo)}`);
    return [...upper, ...lower].join(' ');
  };

  return (
    <g>
      {runs((r) => r.stdBand).map((run, i) => (
        <polygon key={`band-${i}`} points={bandPoints(run)} fill="rgba(255,255,255,0.12)" />
      ))}
      {runs((r) => r.spanMax).map((run, i) => (
        <polyline key={`max-${i}`} points={linePoints(run)} fill="none" stroke="#ff5252" strokeWidth={2} strokeDasharray="6 4" />
      ))}
      {runs((r) => r.spanMin).map((run, i) => (
        <polyline key={`min-${i}`} points={linePoints(run)} fill="none" stroke="#3ddc84" strokeWidth={2} strokeDasharray="6 4" />
      ))}
    </g>
  );
};

const WeatherInPort: React.FC = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id');
  const fromParam = query.get('from');
  const toParam = query.get('to');
  const src = query.get('src');

  const { fetchWeatherInPort } = useWeatherInPortApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<WeatherInPortResponse | null>(null);
  const [includeDrizzle, setIncludeDrizzle] = useState(false);

  useEffect(() => {
    if (!id) return;

    const loadData = async () => {
      setLoading(true);
      setError(null);

      const result = await fetchWeatherInPort(id, src);

      if (result.ok && result.data) {
        setData(result.data);
      } else {
        setError(result.message || 'Failed to load weather in port data');
      }

      setLoading(false);
    };

    loadData();
  }, [id, src, fetchWeatherInPort]);

  // Check if a month is in the voyage period based on from/to params
  const isMonthInPeriod = useCallback((monthKey: string): boolean => {
    if (!fromParam || !toParam) return false;

    const fromMonth = parseInt(fromParam.split('-')[0], 10);
    const toMonth = parseInt(toParam.split('-')[0], 10);
    const month = parseInt(monthKey, 10);

    if (fromMonth <= toMonth) {
      return month >= fromMonth && month <= toMonth;
    } else {
      // Handles year wrap-around (e.g., Nov to Feb)
      return month >= fromMonth || month <= toMonth;
    }
  }, [fromParam, toParam]);

  // Prepare chart data with breakdown by intensity
  const chartData = useMemo(() => {
    if (!data) return [];

    return MONTH_KEYS.map(monthKey => {
      const monthData = data.monthly[monthKey] as MonthlyWeatherData;

      // Year-to-year envelope follows the displayed total: >=0.1 with drizzle on, >=0.2 without
      const spanMin = includeDrizzle ? monthData.precip_time_min : monthData.rain_time_min;
      const spanMax = includeDrizzle ? monthData.precip_time_max : monthData.rain_time_max;
      const std = includeDrizzle ? monthData.precip_time_std : monthData.rain_time_std;
      const displayedTotal = includeDrizzle
        ? monthData.pct_any_precipitation
        : monthData.pct_any_precipitation - getDrizzle(monthData);

      return {
        month: MONTH_NAMES[monthKey],
        monthKey,
        isActive: isMonthInPeriod(monthKey),
        drizzle: getDrizzle(monthData),
        light: getLightAboveDrizzle(monthData),
        moderateLow: monthData.pct_moderate_low,
        moderate: monthData.pct_moderate,
        moderateHigh: monthData.pct_moderate_high,
        heavy: monthData.pct_heavy,
        spanMin: spanMin ?? null,
        spanMax: spanMax ?? null,
        // ±1 std dev band around the displayed total (typical years)
        stdBand: std != null ? [Math.max(0, displayedTotal - std), displayedTotal + std] : null,
      };
    });
  }, [data, includeDrizzle, isMonthInPeriod]);

  // Colors for each intensity category: weather-radar style progression
  // (pale blue drizzle -> dark red heavy). Adjacent pairs CVD-validated.
  const INTENSITY_COLORS: Record<string, string> = {
    drizzle: '#a8ecf7',
    light: '#2fb5da',
    moderateLow: '#ffc107',
    moderate: '#fd7e14',
    moderateHigh: '#dc3545',
    heavy: '#8b0000',
  };

  // Canonical series order (bottom of stack -> top): drizzle -> heavy.
  // Legend and tooltip are forced to this order; Recharts' own ordering is unreliable.
  const SERIES_ORDER: readonly string[] = ['drizzle', 'light', 'moderateLow', 'moderate', 'moderateHigh', 'heavy'];
  const SERIES_LABELS: Record<string, string> = {
    drizzle: 'Drizzle',
    light: 'Light',
    moderateLow: 'Mod. Low',
    moderate: 'Moderate',
    moderateHigh: 'Mod. High',
    heavy: 'Heavy',
  };
  const visibleSeries = includeDrizzle ? SERIES_ORDER : SERIES_ORDER.slice(1);

  if (!id) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>No port ID provided. Please provide an ID in the URL.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-loading">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p>Loading weather in port data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="weather-inport-container">
        <div className="wip-message wip-error">
          <i className="bi bi-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="weather-inport-container">
      <div className="wip-header">
        <h1>Weather In Port Analysis</h1>
        {data && <p className="wip-subtitle">{data.port_name}</p>}
        {fromParam && toParam && (
          <p className="wip-period">
            <i className="bi bi-calendar3"></i>
            Port stay: {fromParam} to {toParam}
          </p>
        )}
      </div>

      {data && (
        <>
          {/* Summary Panel */}
          {fromParam && toParam && (
            <section className="wip-section wip-summary-section">
              <h2>
                <i className="bi bi-graph-up"></i>
                Port Stay Summary
              </h2>
              <div className="summary-cards">
                {(() => {
                  // Calculate weighted monthly averages based on days in each month
                  const [fromMonth, fromDay] = fromParam.split('-').map(Number);
                  const [toMonth, toDay] = toParam.split('-').map(Number);
                  const daysInMonth = [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

                  // Build weighted months array: { month, days }
                  const weightedMonths: { month: number; days: number }[] = [];

                  if (fromMonth === toMonth) {
                    // Same month: just count the days
                    weightedMonths.push({ month: fromMonth, days: toDay - fromDay + 1 });
                  } else if (fromMonth < toMonth) {
                    // Spans multiple months within same year
                    // First month: from fromDay to end of month
                    weightedMonths.push({ month: fromMonth, days: daysInMonth[fromMonth] - fromDay + 1 });
                    // Middle months: full months
                    for (let m = fromMonth + 1; m < toMonth; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    // Last month: from 1 to toDay
                    weightedMonths.push({ month: toMonth, days: toDay });
                  } else {
                    // Year wrap (e.g., Nov to Feb) - not typical but handle it
                    weightedMonths.push({ month: fromMonth, days: daysInMonth[fromMonth] - fromDay + 1 });
                    for (let m = fromMonth + 1; m <= 12; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    for (let m = 1; m < toMonth; m++) {
                      weightedMonths.push({ month: m, days: daysInMonth[m] });
                    }
                    weightedMonths.push({ month: toMonth, days: toDay });
                  }

                  const totalDays = weightedMonths.reduce((sum, wm) => sum + wm.days, 0);

                  // Calculate weighted averages
                  let weightedRain = 0;
                  let weightedAnyPrecip = 0;
                  let weightedDuration = 0;
                  // Year-to-year span across the months of the stay (min of mins, max of maxes)
                  let rainMin: number | null = null;
                  let rainMax: number | null = null;
                  let precipMin: number | null = null;
                  let precipMax: number | null = null;
                  // Day-weighted mean of the monthly year-to-year std devs
                  // (indicative "typical variation", not strict variance propagation)
                  let rainStd: number | null = null;
                  let precipStd: number | null = null;

                  weightedMonths.forEach(({ month, days }) => {
                    const monthData = data.monthly[month.toString()] as MonthlyWeatherData;
                    const weight = days / totalDays;
                    weightedRain += (monthData.pct_any_precipitation - getDrizzle(monthData)) * weight;
                    weightedAnyPrecip += monthData.pct_any_precipitation * weight;
                    weightedDuration += monthData.avg_rain_duration * weight;
                    if (monthData.rain_time_min != null && monthData.rain_time_max != null) {
                      rainMin = rainMin == null ? monthData.rain_time_min : Math.min(rainMin, monthData.rain_time_min);
                      rainMax = rainMax == null ? monthData.rain_time_max : Math.max(rainMax, monthData.rain_time_max);
                    }
                    if (monthData.precip_time_min != null && monthData.precip_time_max != null) {
                      precipMin = precipMin == null ? monthData.precip_time_min : Math.min(precipMin, monthData.precip_time_min);
                      precipMax = precipMax == null ? monthData.precip_time_max : Math.max(precipMax, monthData.precip_time_max);
                    }
                    if (monthData.rain_time_std != null) {
                      rainStd = (rainStd ?? 0) + monthData.rain_time_std * weight;
                    }
                    if (monthData.precip_time_std != null) {
                      precipStd = (precipStd ?? 0) + monthData.precip_time_std * weight;
                    }
                  });

                  return (
                    <>
                      <div className="summary-card">
                        <div className="card-icon">
                          <i className="bi bi-droplet-half"></i>
                        </div>
                        <div className="card-content">
                          <span className="card-value">{weightedRain.toFixed(1)}%</span>
                          <span className="card-label">Time with Rain</span>
                          <span className="card-sublabel">&gt;0.2 mm/h</span>
                          {rainMin != null && rainMax != null && (
                            <span className="card-sublabel">Driest&ndash;wettest year: {rainMin.toFixed(0)}&ndash;{rainMax.toFixed(0)}%</span>
                          )}
                          {rainStd != null && (
                            <span className="card-sublabel">Std dev: &plusmn;{rainStd.toFixed(1)}%</span>
                          )}
                        </div>
                      </div>
                      {includeDrizzle && (
                        <div className="summary-card">
                          <div className="card-icon">
                            <i className="bi bi-cloud-drizzle"></i>
                          </div>
                          <div className="card-content">
                            <span className="card-value">{weightedAnyPrecip.toFixed(1)}%</span>
                            <span className="card-label">Incl. Drizzle</span>
                            <span className="card-sublabel">&gt;0.1 mm/h</span>
                            {precipMin != null && precipMax != null && (
                              <span className="card-sublabel">Driest&ndash;wettest year: {precipMin.toFixed(0)}&ndash;{precipMax.toFixed(0)}%</span>
                            )}
                            {precipStd != null && (
                              <span className="card-sublabel">Std dev: &plusmn;{precipStd.toFixed(1)}%</span>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="summary-card">
                        <div className="card-icon">
                          <i className="bi bi-hourglass-split"></i>
                        </div>
                        <div className="card-content">
                          <span className="card-value">{weightedDuration.toFixed(1)}h</span>
                          <span className="card-label">Avg Rain Duration</span>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            </section>
          )}

          {/* Monthly Precipitation Chart */}
          <section className="wip-section wip-chart-section">
            <div className="chart-header">
              <h2>
                <i className="bi bi-bar-chart-fill"></i>
                Monthly Precipitation
              </h2>
              <div className="chart-toggle">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={includeDrizzle}
                    onChange={(e) => setIncludeDrizzle(e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">
                  Include drizzle (0.1-0.2 mm/h)
                </span>
              </div>
            </div>
            <p className="section-description">
              {includeDrizzle
                ? 'Percentage of hours with any precipitation (>= 0.1 mm/h)'
                : 'Percentage of hours with rain (>= 0.2 mm/h)'}
            </p>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={350}>
                <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis
                    dataKey="month"
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
                  />
                  <YAxis
                    stroke="rgba(255,255,255,0.6)"
                    tick={{ fill: 'rgba(255,255,255,0.8)', fontSize: 12 }}
                    tickFormatter={(value) => `${value}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    cursor={{ fill: 'rgba(255, 255, 255, 0.05)' }}
                    itemSorter={(item) => {
                      const key = String(item.dataKey);
                      const idx = SERIES_ORDER.indexOf(key);
                      if (idx >= 0) return idx;
                      if (key === 'stdBand') return 89; // spread entries last
                      return key === 'spanMin' ? 90 : 91;
                    }}
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid rgba(41, 230, 255, 0.3)',
                      borderRadius: '8px',
                      color: '#fff',
                    }}
                    formatter={(value, name) => {
                      const labels: Record<string, string> = {
                        drizzle: 'Light Drizzle (0.1-0.2 mm/h)',
                        light: 'Light (0.2-1.0 mm/h)',
                        moderateLow: 'Moderate Low (1.0-2.0 mm/h)',
                        moderate: 'Moderate (2.0-3.0 mm/h)',
                        moderateHigh: 'Moderate High (3.0-4.0 mm/h)',
                        heavy: 'Heavy (≥4.0 mm/h)',
                        spanMin: 'Driest year',
                        spanMax: 'Wettest year',
                        stdBand: 'Typical years (±1 std dev)',
                      };
                      if (Array.isArray(value)) {
                        return [`${Number(value[0]).toFixed(1)}–${Number(value[1]).toFixed(1)}%`, labels[name as string] || name];
                      }
                      return [`${Number(value).toFixed(1)}%`, labels[name as string] || name];
                    }}
                    labelFormatter={(label, payload) => {
                      if (!payload || payload.length === 0) return label;
                      let total = 0;
                      payload.forEach(entry => {
                        // Sum stacked segments only - the year-range lines are not part of the total
                        if (SERIES_ORDER.includes(String(entry.dataKey))) {
                          total += Number(entry.value) || 0;
                        }
                      });
                      return `${label} (${total.toFixed(1)}% total)`;
                    }}
                    labelStyle={{ color: '#29e6ff' }}
                  />
                  {/* Always mounted, toggled via `hide`: conditionally mounting the Bar
                      makes Recharts register it last and stack it on top. */}
                  <Bar dataKey="drizzle" stackId="precipitation" fill={INTENSITY_COLORS.drizzle} hide={!includeDrizzle} />
                  <Bar dataKey="light" stackId="precipitation" fill={INTENSITY_COLORS.light} />
                  <Bar dataKey="moderateLow" stackId="precipitation" fill={INTENSITY_COLORS.moderateLow} />
                  <Bar dataKey="moderate" stackId="precipitation" fill={INTENSITY_COLORS.moderate} />
                  <Bar dataKey="moderateHigh" stackId="precipitation" fill={INTENSITY_COLORS.moderateHigh} />
                  <Bar dataKey="heavy" stackId="precipitation" fill={INTENSITY_COLORS.heavy} radius={[4, 4, 0, 0]} />
                  {/* ±1 std dev band + envelope values for the TOOLTIP only —
                      the visible drawing happens in SpreadOverlay, which
                      extends the runs to the bar edges. */}
                  <Area
                    dataKey="stdBand"
                    fill="transparent"
                    stroke="none"
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="spanMax"
                    stroke="transparent"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <Line
                    dataKey="spanMin"
                    stroke="transparent"
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  <SpreadOverlay rows={chartData} />
                </ComposedChart>
              </ResponsiveContainer>
              {/* Custom legend: Recharts 3 ignores an explicit Legend payload and
                  sorts items itself, so the order is rendered by hand instead. */}
              <div className="chart-legend">
                {visibleSeries.map(key => (
                  <span key={key} className="chart-legend-item">
                    <span className="chart-legend-swatch" style={{ background: INTENSITY_COLORS[key] }} />
                    {SERIES_LABELS[key]}
                  </span>
                ))}
                <span className="chart-legend-item">
                  <span className="chart-legend-dash" style={{ borderTopColor: '#ff5252' }} />
                  Wettest year
                </span>
                <span className="chart-legend-item">
                  <span className="chart-legend-dash" style={{ borderTopColor: '#3ddc84' }} />
                  Driest year
                </span>
                <span className="chart-legend-item">
                  <span className="chart-legend-band" />
                  ±1 std dev
                </span>
              </div>
            </div>
            {fromParam && toParam && (
              <p className="chart-note">
                <span className="active-bar"></span> Highlighted bars indicate port stay period
              </p>
            )}
          </section>

          {/* Monthly Precipitation Intensity Distribution */}
          <section className="wip-section">
            <h2>
              <i className="bi bi-cloud-rain"></i>
              Monthly Precipitation Intensity
            </h2>
            <p className="section-description">
              Percentage of hours in each precipitation intensity category (based on 10-year climatology)
            </p>
            <div className="table-responsive">
              <table className="wip-table wip-intensity-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Intensity</th>
                    <th className="sticky-col-2">Threshold</th>
                    {MONTH_KEYS.map(key => (
                      <th
                        key={key}
                        className={isMonthInPeriod(key) ? 'active-month' : ''}
                      >
                        {MONTH_NAMES[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {INTENSITY_KEYS.map(intensityKey => (
                    <tr key={intensityKey} className={`intensity-row intensity-${intensityKey.replace('pct_', '')}`}>
                      <td className="sticky-col intensity-label">{INTENSITY_LABELS[intensityKey]}</td>
                      <td className="sticky-col-2 threshold-cell">{INTENSITY_THRESHOLDS[intensityKey]}</td>
                      {MONTH_KEYS.map(monthKey => {
                        const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                        // "Light" is displayed as the disjoint 0.2-1.0 share
                        const value = intensityKey === 'pct_light'
                          ? getLightAboveDrizzle(monthData)
                          : intensityKey === 'pct_light_drizzle'
                            ? getDrizzle(monthData)
                            : monthData[intensityKey as keyof MonthlyWeatherData] as number;
                        return (
                          <td
                            key={monthKey}
                            className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                          >
                            {value.toFixed(1)}%
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Total significant rain row (>1 mm/h) */}
                  <tr className="total-row">
                    <td className="sticky-col intensity-label">Rain (&gt;1 mm/h)</td>
                    <td className="sticky-col-2 threshold-cell">&gt;= 1.0 mm/h</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      const significantRain = monthData.pct_moderate_low + monthData.pct_moderate +
                                              monthData.pct_moderate_high + monthData.pct_heavy;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {significantRain.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Monthly Rain Event Statistics */}
          <section className="wip-section">
            <h2>
              <i className="bi bi-clock-history"></i>
              Rain Event Duration Statistics
            </h2>
            <p className="section-description">
              Average duration and frequency of rain events by month
            </p>
            <div className="table-responsive">
              <table className="wip-table wip-duration-table">
                <thead>
                  <tr>
                    <th className="sticky-col">Statistic</th>
                    {MONTH_KEYS.map(key => (
                      <th
                        key={key}
                        className={isMonthInPeriod(key) ? 'active-month' : ''}
                      >
                        {MONTH_NAMES[key]}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="sticky-col stat-label">Avg Duration (hours)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.avg_rain_duration.toFixed(1)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td className="sticky-col stat-label">Events/Day</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.avg_daily_events.toFixed(2)}
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Short Events (1-2h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_short_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Medium Events (3-6h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_medium_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                  <tr className="event-type-row">
                    <td className="sticky-col stat-label">Long Events (&gt;6h)</td>
                    {MONTH_KEYS.map(monthKey => {
                      const monthData = data.monthly[monthKey] as MonthlyWeatherData;
                      return (
                        <td
                          key={monthKey}
                          className={isMonthInPeriod(monthKey) ? 'active-month' : ''}
                        >
                          {monthData.pct_long_events.toFixed(1)}%
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Legend */}
          <section className="wip-section wip-legend">
            <h2>
              <i className="bi bi-info-circle"></i>
              Understanding the Data
            </h2>
            <div className="legend-content">
              <div className="legend-group">
                <h3>Precipitation Intensity Categories</h3>
                <ul>
                  <li><span className="legend-color dry"></span><strong>Dry:</strong> No significant precipitation (&lt; 0.1 mm/h)</li>
                  <li><span className="legend-color drizzle"></span><strong>Light Drizzle:</strong> Very light drizzle (0.1 - 0.2 mm/h) — not counted as rain, since the ERA5 weather model over-produces drizzle compared to observations</li>
                  <li><span className="legend-color light"></span><strong>Light:</strong> Light rain (0.2 - 1.0 mm/h)</li>
                  <li><span className="legend-color moderate-low"></span><strong>Moderate Low:</strong> Light to moderate rain (1.0 - 2.0 mm/h)</li>
                  <li><span className="legend-color moderate"></span><strong>Moderate:</strong> Moderate rain (2.0 - 3.0 mm/h)</li>
                  <li><span className="legend-color moderate-high"></span><strong>Moderate High:</strong> Moderate to heavy rain (3.0 - 4.0 mm/h)</li>
                  <li><span className="legend-color heavy"></span><strong>Heavy:</strong> Heavy rain or showers (&gt;= 4.0 mm/h)</li>
                </ul>
              </div>
              <div className="legend-group">
                <h3>Rain Event Duration</h3>
                <ul>
                  <li><strong>Short Events:</strong> Rain lasting 1-2 hours</li>
                  <li><strong>Medium Events:</strong> Rain lasting 3-6 hours</li>
                  <li><strong>Long Events:</strong> Rain lasting more than 6 hours</li>
                </ul>
              </div>
              <div className="legend-group">
                <h3>Port Stay Summary Calculations</h3>
                <ul>
                  <li><strong>Time with Rain:</strong> Weighted average % of hours with rain (&gt;= 0.2 mm/h), weighted by days in each month of the stay</li>
                  <li><strong>Incl. Drizzle</strong> (shown when the drizzle toggle is on): Weighted average % of hours with any precipitation (&gt;= 0.1 mm/h), including light drizzle</li>
                  <li><strong>Avg Rain Duration:</strong> Weighted average duration of rain events based on days in each month</li>
                  <li><strong>Driest/wettest year:</strong> The observed extremes across the 10-year window for the same months (full range, not a standard deviation) — individual years can differ substantially from the average</li>
                  <li><strong>Std dev:</strong> Year-to-year standard deviation of the monthly value (about two thirds of years fall within &plusmn;1 std dev of the average); day-weighted across the months of the stay</li>
                </ul>
              </div>
              <div className="legend-note">
                <i className="bi bi-database"></i>
                <span>
                  Data based on 10-year ERA5 climatological averages for this port location. Contains modified
                  Copernicus Climate Change Service information (2026); neither the European Commission nor ECMWF
                  is responsible for any use made of it.
                </span>
              </div>
            </div>
          </section>

          {fromParam && toParam && (
            <p className="table-note">
              <span className="active-indicator"></span> Highlighted months indicate port stay period
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default WeatherInPort;
